import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { z } from "zod";

export const opencodeMessageRoleSchema = z
  .enum(["user", "assistant"])
  .describe("OpencodeMessageRole");

export const opencodeSessionSchema = z
  .object({
    id: z.uuid(),
    projectId: z.uuid(),
    createdAt: z.date(),
    updatedAt: z.date(),
    deletedAt: z.date().nullable(),
    opencodeId: z.string(),
    title: z.string().nullable(),
  })
  .describe("OpencodeSession");

export const opencodeMessageSchema = z
  .object({
    id: z.uuid(),
    userId: z.uuid(),
    opencodeSessionUuid: z.uuid(),
    createdAt: z.date(),
    updatedAt: z.date(),
    deletedAt: z.date().nullable(),
    opencodeId: z.string(),
    opencodeSessionId: z.string(),
    opencodeParentId: z.string().nullable(),
    completedAt: z.date().nullable(),
    role: opencodeMessageRoleSchema,
    finishReason: z.string().nullable(),
    error: z.unknown().nullable(),
    cost: z.number().nullable(),
    tokensInput: z.number().nullable(),
    tokensOutput: z.number().nullable(),
    tokensReasoning: z.number().nullable(),
  })
  .describe("OpencodeMessage");

const opencodeMetadataSchema = z.record(z.string(), z.unknown());

const opencodeFilePartSourceTextSchema = z.object({
  value: z.string(),
  start: z.number(),
  end: z.number(),
});

const opencodeRangeSchema = z.object({
  start: z.object({
    line: z.number(),
    character: z.number(),
  }),
  end: z.object({
    line: z.number(),
    character: z.number(),
  }),
});

const opencodeFilePartSourceSchema = z.discriminatedUnion("type", [
  z.object({
    text: opencodeFilePartSourceTextSchema,
    type: z.literal("file"),
    path: z.string(),
  }),
  z.object({
    text: opencodeFilePartSourceTextSchema,
    type: z.literal("symbol"),
    path: z.string(),
    range: opencodeRangeSchema,
    name: z.string(),
    kind: z.number(),
  }),
]);

export const opencodeFilePartSchema = z
  .object({
    id: z.string(),
    sessionID: z.string(),
    messageID: z.string(),
    type: z.literal("file"),
    mime: z.string(),
    filename: z.string().optional(),
    url: z.string(),
    source: opencodeFilePartSourceSchema.optional(),
  })
  .describe("OpencodeFilePart");

export const opencodeToolStateSchema = z
  .discriminatedUnion("status", [
    z.object({
      status: z.literal("pending"),
      input: opencodeMetadataSchema,
      raw: z.string(),
    }),
    z.object({
      status: z.literal("running"),
      input: opencodeMetadataSchema,
      title: z.string().optional(),
      metadata: opencodeMetadataSchema.optional(),
      time: z.object({
        start: z.number(),
      }),
    }),
    z.object({
      status: z.literal("completed"),
      input: opencodeMetadataSchema,
      output: z.string(),
      title: z.string(),
      metadata: opencodeMetadataSchema,
      time: z.object({
        start: z.number(),
        end: z.number(),
        compacted: z.number().optional(),
      }),
      attachments: z.array(opencodeFilePartSchema).optional(),
    }),
    z.object({
      status: z.literal("error"),
      input: opencodeMetadataSchema,
      error: z.string(),
      metadata: opencodeMetadataSchema.optional(),
      time: z.object({
        start: z.number(),
        end: z.number(),
      }),
    }),
  ])
  .describe("OpencodeToolState");

const opencodeApiErrorSchema = z.object({
  name: z.literal("APIError"),
  data: z.object({
    message: z.string(),
    statusCode: z.number().optional(),
    isRetryable: z.boolean(),
    responseHeaders: z.record(z.string(), z.string()).optional(),
    responseBody: z.string().optional(),
  }),
});

export const opencodeTextPartSchema = z
  .object({
    id: z.string(),
    sessionID: z.string(),
    messageID: z.string(),
    type: z.literal("text"),
    text: z.string(),
    synthetic: z.boolean().optional(),
    ignored: z.boolean().optional(),
    time: z
      .object({
        start: z.number(),
        end: z.number().optional(),
      })
      .optional(),
    metadata: opencodeMetadataSchema.optional(),
  })
  .describe("OpencodeTextPart");

export const opencodeSubtaskPartSchema = z
  .object({
    id: z.string(),
    sessionID: z.string(),
    messageID: z.string(),
    type: z.literal("subtask"),
    prompt: z.string(),
    description: z.string(),
    agent: z.string(),
  })
  .describe("OpencodeSubtaskPart");

export const opencodeReasoningPartSchema = z
  .object({
    id: z.string(),
    sessionID: z.string(),
    messageID: z.string(),
    type: z.literal("reasoning"),
    text: z.string(),
    metadata: opencodeMetadataSchema.optional(),
    time: z.object({
      start: z.number(),
      end: z.number().optional(),
    }),
  })
  .describe("OpencodeReasoningPart");

export const opencodeToolPartSchema = z
  .object({
    id: z.string(),
    sessionID: z.string(),
    messageID: z.string(),
    type: z.literal("tool"),
    callID: z.string(),
    tool: z.string(),
    state: opencodeToolStateSchema,
    metadata: opencodeMetadataSchema.optional(),
  })
  .describe("OpencodeToolPart");

export const opencodeStepStartPartSchema = z
  .object({
    id: z.string(),
    sessionID: z.string(),
    messageID: z.string(),
    type: z.literal("step-start"),
    snapshot: z.string().optional(),
  })
  .describe("OpencodeStepStartPart");

export const opencodeStepFinishPartSchema = z
  .object({
    id: z.string(),
    sessionID: z.string(),
    messageID: z.string(),
    type: z.literal("step-finish"),
    reason: z.string(),
    snapshot: z.string().optional(),
    cost: z.number(),
    tokens: z.object({
      input: z.number(),
      output: z.number(),
      reasoning: z.number(),
      cache: z.object({
        read: z.number(),
        write: z.number(),
      }),
    }),
  })
  .describe("OpencodeStepFinishPart");

export const opencodeSnapshotPartSchema = z
  .object({
    id: z.string(),
    sessionID: z.string(),
    messageID: z.string(),
    type: z.literal("snapshot"),
    snapshot: z.string(),
  })
  .describe("OpencodeSnapshotPart");

export const opencodePatchPartSchema = z
  .object({
    id: z.string(),
    sessionID: z.string(),
    messageID: z.string(),
    type: z.literal("patch"),
    hash: z.string(),
    files: z.array(z.string()),
  })
  .describe("OpencodePatchPart");

export const opencodeAgentPartSchema = z
  .object({
    id: z.string(),
    sessionID: z.string(),
    messageID: z.string(),
    type: z.literal("agent"),
    name: z.string(),
    source: opencodeFilePartSourceTextSchema.optional(),
  })
  .describe("OpencodeAgentPart");

export const opencodeRetryPartSchema = z
  .object({
    id: z.string(),
    sessionID: z.string(),
    messageID: z.string(),
    type: z.literal("retry"),
    attempt: z.number(),
    error: opencodeApiErrorSchema,
    time: z.object({
      created: z.number(),
    }),
  })
  .describe("OpencodeRetryPart");

export const opencodeCompactionPartSchema = z
  .object({
    id: z.string(),
    sessionID: z.string(),
    messageID: z.string(),
    type: z.literal("compaction"),
    auto: z.boolean(),
  })
  .describe("OpencodeCompactionPart");

export const opencodePartDataSchema = z
  .discriminatedUnion("type", [
    opencodeTextPartSchema,
    opencodeSubtaskPartSchema,
    opencodeReasoningPartSchema,
    opencodeFilePartSchema,
    opencodeToolPartSchema,
    opencodeStepStartPartSchema,
    opencodeStepFinishPartSchema,
    opencodeSnapshotPartSchema,
    opencodePatchPartSchema,
    opencodeAgentPartSchema,
    opencodeRetryPartSchema,
    opencodeCompactionPartSchema,
  ])
  .describe("OpencodePartData");

export const opencodeMessagePartSchema = z
  .object({
    id: z.uuid(),
    opencodeMessageUuid: z.uuid(),
    opencodeId: z.string(),
    createdAt: z.date(),
    updatedAt: z.date(),
    deletedAt: z.date().nullable(),
    partData: opencodePartDataSchema,
  })
  .describe("OpencodeMessagePart");

export const sessionMessageWithPartsSchema = z
  .object({
    message: opencodeMessageSchema,
    parts: z.array(opencodeMessagePartSchema),
  })
  .describe("SessionMessageWithParts");

// ------------------------------------------------------------
// Tool input schemas
// ------------------------------------------------------------

export const opencodeTodoItemStatusSchema = z
  .enum(["pending", "in_progress", "completed", "cancelled"])
  .describe("OpencodeTodoItemStatus");

export const opencodeTodoItemSchema = z
  .object({
    id: z.string(),
    content: z.string(),
    status: opencodeTodoItemStatusSchema,
    activeForm: z.string().optional(),
  })
  .describe("OpencodeTodoItem");

export const opencodeTodoToolInputSchema = z
  .object({
    todos: z.array(opencodeTodoItemSchema),
    merge: z.boolean().optional(),
  })
  .describe("OpencodeTodoToolInput");

// ------------------------------------------------------------
// Upstream schemas
// ------------------------------------------------------------

// NOTE (Max, 2026-02-10): 'upstream' means that the data is coming directly from Opencode
// and is not yet transformed into our internal representation.

export const opencodeUpstreamMessage = z
  .object({
    id: z.string().min(1),
    sessionID: z.string().min(1),
    role: z.enum(["user", "assistant"]),
    time: z.object({
      created: z.number(),
      completed: z.number().optional(),
    }),
    parentID: z.string().min(1).optional(),
    finish: z.string().min(1).optional(),
    cost: z.number().optional(),
    tokens: z
      .object({
        input: z.number(),
        output: z.number(),
        reasoning: z.number().optional(),
      })
      .optional(),
    error: z.unknown().optional(),
  })
  .passthrough()
  .describe("OpencodeUpstreamMessage");

export const opencodeUpstreamPart = z
  .object({
    id: z.string().min(1),
    messageID: z.string().min(1),
    sessionID: z.string().min(1),
  })
  .passthrough()
  .describe("OpencodeUpstreamPart");

const messageUpdatedEventSchema = z.object({
  type: z.literal("message.updated"),
  properties: z.object({
    info: opencodeUpstreamMessage,
  }),
});

const messageCreatedEventSchema = z.object({
  type: z.literal("message.created"),
  properties: z.object({
    info: opencodeUpstreamMessage,
  }),
});

const messagePartUpdatedEventSchema = z.object({
  type: z.literal("message.part.updated"),
  properties: z.object({
    part: opencodeUpstreamPart,
  }),
});

const messagePartAddedEventSchema = z.object({
  type: z.literal("message.part.added"),
  properties: z.object({
    part: opencodeUpstreamPart,
  }),
});

const messagePartCreatedEventSchema = z.object({
  type: z.literal("message.part.created"),
  properties: z.object({
    part: opencodeUpstreamPart,
  }),
});

export const messagePartDeltaEventSchema = z.object({
  type: z.literal("message.part.delta"),
  properties: z.object({
    sessionID: z.string(),
    messageID: z.string(),
    partID: z.string(),
    field: z.string(),
    delta: z.string(),
  }),
});

export const opencodePersistenceEventSchema = z
  .discriminatedUnion("type", [
    messageUpdatedEventSchema,
    messageCreatedEventSchema,
    messagePartUpdatedEventSchema,
    messagePartAddedEventSchema,
    messagePartCreatedEventSchema,
  ])
  .describe("OpencodePersistenceEvent");

// ---------------------------------------------------------------------------
// Non-message event schemas
// ---------------------------------------------------------------------------

// -- Session lifecycle events -----------------------------------------------

const opencodeUpstreamSession = z
  .object({
    id: z.string(),
    projectID: z.string().optional(),
    directory: z.string().optional(),
    title: z.string().optional(),
    version: z.string().optional(),
    parentID: z.string().optional(),
    time: z
      .object({
        created: z.number(),
        updated: z.number(),
        compacting: z.number().optional(),
      })
      .passthrough()
      .optional(),
  })
  .passthrough();

const sessionStatusValueSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("idle") }),
  z.object({ type: z.literal("busy") }),
  z.object({
    type: z.literal("retry"),
    attempt: z.number(),
    message: z.string(),
    next: z.number(),
  }),
]);

const sessionStatusEventSchema = z.object({
  type: z.literal("session.status"),
  properties: z.object({
    sessionID: z.string(),
    status: sessionStatusValueSchema,
  }),
});

const sessionIdleEventSchema = z.object({
  type: z.literal("session.idle"),
  properties: z.object({
    sessionID: z.string(),
  }),
});

const sessionCompactedEventSchema = z.object({
  type: z.literal("session.compacted"),
  properties: z.object({
    sessionID: z.string(),
  }),
});

export const sessionCreatedEventSchema = z.object({
  type: z.literal("session.created"),
  properties: z.object({
    info: opencodeUpstreamSession,
  }),
});

export const sessionUpdatedEventSchema = z.object({
  type: z.literal("session.updated"),
  properties: z.object({
    info: opencodeUpstreamSession,
  }),
});

const sessionDeletedEventSchema = z.object({
  type: z.literal("session.deleted"),
  properties: z.object({
    info: opencodeUpstreamSession,
  }),
});

const sessionDiffEventSchema = z.object({
  type: z.literal("session.diff"),
  properties: z.object({
    sessionID: z.string(),
    diff: z.array(
      z.object({
        file: z.string(),
        before: z.string(),
        after: z.string(),
        additions: z.number(),
        deletions: z.number(),
      }),
    ),
  }),
});

const sessionErrorEventSchema = z.object({
  type: z.literal("session.error"),
  properties: z.object({
    sessionID: z.string().optional(),
    error: z.unknown().optional(),
  }),
});

// -- Permission events ------------------------------------------------------

const permissionUpdatedEventSchema = z.object({
  type: z.literal("permission.updated"),
  properties: z
    .object({
      id: z.string(),
      type: z.string(),
      sessionID: z.string(),
      messageID: z.string(),
      callID: z.string().optional(),
      title: z.string(),
      metadata: z.record(z.string(), z.unknown()),
      time: z.object({ created: z.number() }),
    })
    .passthrough(),
});

const permissionRepliedEventSchema = z.object({
  type: z.literal("permission.replied"),
  properties: z.object({
    sessionID: z.string(),
    permissionID: z.string(),
    response: z.string(),
  }),
});

// -- Question events --------------------------------------------------------

export const questionAskedEventSchema = z.object({
  type: z.literal("question.asked"),
  properties: z
    .object({
      id: z.string(),
      sessionID: z.string(),
      tool: z.object({ callID: z.string() }).passthrough(),
    })
    .passthrough(),
});

// -- Other events -----------------------------------------------------------

const todoUpdatedEventSchema = z.object({
  type: z.literal("todo.updated"),
  properties: z.object({
    sessionID: z.string(),
    todos: z.array(
      z.object({
        id: z.string(),
        content: z.string(),
        status: z.string(),
        priority: z.string(),
      }),
    ),
  }),
});

const fileEditedEventSchema = z.object({
  type: z.literal("file.edited"),
  properties: z.object({
    file: z.string(),
  }),
});

// ---------------------------------------------------------------------------
// Unified opencode event schema
// ---------------------------------------------------------------------------

export const opencodeEventSchema = z.discriminatedUnion("type", [
  // Persisted message events
  messageUpdatedEventSchema,
  messageCreatedEventSchema,
  messagePartUpdatedEventSchema,
  messagePartAddedEventSchema,
  messagePartCreatedEventSchema,
  messagePartDeltaEventSchema,
  // Session lifecycle events
  sessionStatusEventSchema,
  sessionIdleEventSchema,
  sessionCompactedEventSchema,
  sessionCreatedEventSchema,
  sessionUpdatedEventSchema,
  sessionDeletedEventSchema,
  sessionDiffEventSchema,
  sessionErrorEventSchema,
  // Permission events
  permissionUpdatedEventSchema,
  permissionRepliedEventSchema,
  // Question events
  questionAskedEventSchema,
  // Other events
  todoUpdatedEventSchema,
  fileEditedEventSchema,
]);

export type OpencodeEvent = z.infer<typeof opencodeEventSchema>;

// ---------------------------------------------------------------------------
// Persisted event subset — only message events are persisted to storage.
// ---------------------------------------------------------------------------

export type PersistedOpencodeEvent = z.infer<
  typeof opencodePersistenceEventSchema
>;

export function isPersistedEvent(
  event: OpencodeEvent,
): event is PersistedOpencodeEvent {
  return opencodePersistenceEventSchema.safeParse(event).success;
}

export function getSessionId(event: PersistedOpencodeEvent): string {
  return exhaustiveSwitch(event)({
    "message.updated": (event) => event.properties.info.sessionID,
    "message.created": (event) => event.properties.info.sessionID,
    "message.part.updated": (event) => event.properties.part.sessionID,
    "message.part.added": (event) => event.properties.part.sessionID,
    "message.part.created": (event) => event.properties.part.sessionID,
  });
}

export function getSessionIdFromOpencodeEvent(
  event: OpencodeEvent,
): string | undefined {
  return exhaustiveSwitch(event)({
    "message.updated": (event) => event.properties.info.sessionID,
    "message.created": (event) => event.properties.info.sessionID,
    "message.part.updated": (event) => event.properties.part.sessionID,
    "message.part.added": (event) => event.properties.part.sessionID,
    "message.part.created": (event) => event.properties.part.sessionID,
    "message.part.delta": (event) => event.properties.sessionID,
    "session.status": (event) => event.properties.sessionID,
    "session.idle": (event) => event.properties.sessionID,
    "session.compacted": (event) => event.properties.sessionID,
    "session.created": (event) => event.properties.info.id,
    "session.updated": (event) => event.properties.info.id,
    "session.deleted": (event) => event.properties.info.id,
    "session.diff": (event) => event.properties.sessionID,
    "session.error": (event) => event.properties.sessionID,
    "permission.updated": (event) => event.properties.sessionID,
    "permission.replied": (event) => event.properties.sessionID,
    "question.asked": (event) => event.properties.sessionID,
    "todo.updated": (event) => event.properties.sessionID,
    "file.edited": () => undefined,
  });
}
